import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useScroll, useSpring, useMotionValueEvent } from "framer-motion";
import { ChevronUp } from "lucide-react";

export default function ScrollToTopButton() {
  const [isVisible, setIsVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isScrollingToTop, setIsScrollingToTop] = useState(false);
  const [scrollPercent, setScrollPercent] = useState(0);
  const { scrollYProgress } = useScroll();
  const scrollAnimationFrameRef = useRef(null);

  // Smooth the scroll progress for the ring animation
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  // Track raw scroll progress as a whole-number percentage for the label
  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    setScrollPercent(Math.round(latest * 100));
  });

  useEffect(() => {
    const syncModalState = () => {
      const modalOpen =
        document.body.classList.contains("demo-modal-open") ||
        document.body.classList.contains("video-modal-open");

      setIsModalOpen(modalOpen);
      if (modalOpen) {
        setIsVisible(false);
      }
    };

    syncModalState();

    const observer = new MutationObserver(syncModalState);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  // --- UPDATED: Use useScroll to track visibility without forced reflows ---
  useEffect(() => {
    // We use a separate MotionValue from useScroll to calculate visibility
    // This avoids reading window.pageYOffset directly inside a scroll listener
    const checkVisibility = (latestProgress) => {
      const modalOpen =
        document.body.classList.contains("demo-modal-open") ||
        document.body.classList.contains("video-modal-open");

      if (modalOpen) {
        setIsVisible(false);
        return;
      }

      if (isScrollingToTop) {
        setIsVisible(true);
        return;
      }

      // Framer Motion's scrollYProgress goes from 0 to 1.
      // 400px is roughly 10% of a 4000px page. We use 0.1 as a proxy.
      // If your page is shorter/longer, adjust this number.
      setIsVisible(latestProgress > 0.1);
    };

    // We use useMotionValueEvent on scrollYProgress to handle visibility
    // This is heavily optimized by Framer Motion
    const unsubscribe = scrollYProgress.on("change", checkVisibility);
    
    // Run it once immediately to set initial state
    checkVisibility(scrollYProgress.get());

    return () => unsubscribe();
  }, [isScrollingToTop, scrollYProgress]);

  useEffect(() => {
    return () => {
      if (scrollAnimationFrameRef.current) {
        cancelAnimationFrame(scrollAnimationFrameRef.current);
      }
    };
  }, []);

  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

  const scrollToTop = () => {
    if (isScrollingToTop) return;

    const startY = window.scrollY;
    if (startY <= 0) return;

    const duration = 800;
    const startTime = performance.now();
    setIsScrollingToTop(true);

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutCubic(progress);
      const nextY = Math.max(0, startY * (1 - easedProgress));

      window.scrollTo(0, nextY);

      if (progress < 1 && nextY > 0) {
        scrollAnimationFrameRef.current = requestAnimationFrame(animate);
        return;
      }

      window.scrollTo(0, 0);
      setIsScrollingToTop(false);
      scrollAnimationFrameRef.current = null;
    };

    scrollAnimationFrameRef.current = requestAnimationFrame(animate);
  };

  return (
    <AnimatePresence>
      {isVisible && !isModalOpen && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 40 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 40 }}
          className="scroll-to-top-button fixed right-4 bottom-4 z-50 sm:right-6 sm:bottom-4">
          <button
            type="button"
            onClick={scrollToTop}
            disabled={isScrollingToTop}
            aria-label="Scroll to top"
            className="group relative flex h-16 w-16 cursor-pointer items-center justify-center rounded-full bg-white shadow-[0_10px_30px_rgba(0,0,0,0.15)] transition-all hover:shadow-[0_15px_40px_rgba(0,0,0,0.2)] disabled:cursor-not-allowed disabled:opacity-90 sm:h-25 sm:w-25">
            {/* --- ANIMATED PROGRESS RING --- */}
            <svg className="absolute inset-0 h-full w-full -rotate-90" viewBox="0 0 80 80">
              {/* Static Background Track */}
              <circle
                cx="40"
                cy="40"
                r="37"
                stroke="currentColor"
                strokeWidth="3"
                fill="transparent"
                className="text-gray-100"
              />
              {/* Animated Gradient Stroke */}
              <motion.circle
                cx="40"
                cy="40"
                r="37"
                stroke="url(#gradient)"
                strokeWidth="3"
                fill="transparent"
                strokeDasharray="1"
                pathLength="1"
                style={{ pathLength: scaleX }}
                strokeLinecap="round"
              />
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#415DAA" /> {/* Primary Blue */}
                  <stop offset="100%" stopColor="#7CB342" /> {/* Primary Green */}
                </linearGradient>
              </defs>
            </svg>

            {/* --- BUTTON CONTENT --- */}
              <div className="relative z-10 flex flex-col items-center justify-center text-gray-800 transition-transform duration-300 group-hover:-translate-y-1">
                <ChevronUp 
                  size={24} 
                  strokeWidth={2.5} 
                  className="text-[#7CB342] sm:hidden" 
                />
                <ChevronUp 
                  size={42} 
                  strokeWidth={2.5} 
                  className="text-[#7CB342] hidden sm:block" 
                />
                <span className="text-sm font-semibold leading-none text-gray-600 mt-0.5 sm:text-lg sm:mt-1">
                  {scrollPercent}%
                </span>
              </div>

            {/* --- HOVER GLOW EFFECT --- */}
            <motion.div
              className="absolute inset-0 rounded-full bg-linear-to-br from-[#2546A1]/20 to-[#7CB342]/20 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
              animate={{ scale: [1, 1.15, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}